/**
 * Timezone-safe calendar helpers, plus Arabic display formatting.
 *
 * The core functions mirror server/src/lib/dates.js exactly — the server
 * validates dates and the client displays them, so they must agree on what
 * "today" is. Keep the two in sync.
 *
 * The rule that matters: never derive a calendar date from toISOString(), which
 * returns the UTC day and is wrong in Damascus between midnight and 03:00.
 */

export const APP_TIME_ZONE = 'Asia/Damascus';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isValidDateStr(value) {
  if (typeof value !== 'string' || !DATE_PATTERN.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day
  );
}

export function isValidTimeStr(value) {
  return typeof value === 'string' && TIME_PATTERN.test(value);
}

/** The calendar date in `timeZone` at instant `date`, as 'YYYY-MM-DD'. */
export function dateStrInZone(date = new Date(), timeZone = APP_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const lookup = {};
  for (const part of parts) lookup[part.type] = part.value;
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}

/** Current wall-clock time in Damascus as 'HH:MM', 24-hour. For comparisons. */
export function currentTimeInZone(date = new Date(), timeZone = APP_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const lookup = {};
  for (const part of parts) lookup[part.type] = part.value;
  const hour = lookup.hour === '24' ? '00' : lookup.hour;
  return `${hour}:${lookup.minute}`;
}

/** Current time in Damascus as 'HH:MM:SS', for the live header clock. */
export function currentClockInZone(date = new Date(), timeZone = APP_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const lookup = {};
  for (const part of parts) lookup[part.type] = part.value;
  const hour = lookup.hour === '24' ? '00' : lookup.hour;
  return `${hour}:${lookup.minute}:${lookup.second}`;
}

/**
 * A Date at UTC noon on the given day. Noon leaves a 12-hour buffer either side
 * so no zone offset can shift the value into an adjacent day when formatted.
 */
export function dateFromStr(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`);
}

/** Shifts 'YYYY-MM-DD' by n days, correct across month, year and leap boundaries. */
export function addDays(dateStr, n) {
  const [year, month, day] = dateStr.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + n));
  const yyyy = String(shifted.getUTCFullYear()).padStart(4, '0');
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(shifted.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function todayStr(timeZone = APP_TIME_ZONE) {
  return dateStrInZone(new Date(), timeZone);
}

/* -------------------------------------------------------- Display formats */

/*
 * Arabic locales default to Arabic-Indic numerals (١٣), but times, counts and
 * printed page numbers throughout this application use Latin digits. Mixing the
 * two on one screen reads as a defect, so `-u-nu-latn` pins every formatted date
 * to Latin digits as well.
 */
const AR_LOCALE = 'ar-SY-u-nu-latn';
const AR_HIJRI_LOCALE = 'ar-SA-u-ca-islamic-umalqura-nu-latn';

/** Full Arabic date, e.g. "الخميس، 13 آب 2026". */
export function formatArabicDate(dateStr) {
  if (!isValidDateStr(dateStr)) return '';
  return new Intl.DateTimeFormat(AR_LOCALE, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC', // The value is already anchored at UTC noon.
  }).format(dateFromStr(dateStr));
}

/**
 * Full Arabic date for the printed schedule, e.g. "الخميس، ١٣ آب ٢٠٢٦".
 *
 * Uses Arabic-Indic numerals, which is the convention for the date line of an
 * official Arabic document. Times, the page marker and any figures inside the
 * table stay in Latin digits — that combination is what the governorate's
 * printed schedule uses.
 */
export function formatArabicDatePrint(dateStr) {
  if (!isValidDateStr(dateStr)) return '';
  return new Intl.DateTimeFormat('ar-SY', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(dateFromStr(dateStr));
}

/** Short Arabic date without the weekday, for compact contexts. */
export function formatArabicDateShort(dateStr) {
  if (!isValidDateStr(dateStr)) return '';
  return new Intl.DateTimeFormat(AR_LOCALE, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(dateFromStr(dateStr));
}

/**
 * Hijri date. Not every browser ships the islamic calendar; callers render this
 * only when it returns a value.
 */
export function formatHijriDate(dateStr) {
  if (!isValidDateStr(dateStr)) return '';
  try {
    const formatted = new Intl.DateTimeFormat(AR_HIJRI_LOCALE, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(dateFromStr(dateStr));
    return formatted ? `${formatted} هـ` : '';
  } catch {
    return '';
  }
}

/**
 * 24-hour storage value to Arabic 12-hour display.
 * '09:05' → '9:05 ص',  '14:30' → '2:30 م',  '00:15' → '12:15 ص'
 */
export function formatTime12(time) {
  if (!isValidTimeStr(time)) return time || '';
  const [hourStr, minute] = time.split(':');
  const hour = Number(hourStr);
  const period = hour < 12 ? 'ص' : 'م';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${period}`;
}

/** True when `dateStr` is today in Damascus. */
export function isToday(dateStr) {
  return dateStr === todayStr();
}

/** Relative label for the date heading: أمس / اليوم / غداً, otherwise null. */
export function relativeDayLabel(dateStr) {
  const today = todayStr();
  if (dateStr === today) return 'اليوم';
  if (dateStr === addDays(today, -1)) return 'أمس';
  if (dateStr === addDays(today, 1)) return 'غداً';
  return null;
}
